import {useEffect, useMemo, useRef, useState} from "react";
import {FiArrowRightCircle, FiCalendar, FiEdit, FiPlus, FiSlash} from "react-icons/fi";
import {useNavigate} from "react-router-dom";
import {toast} from "react-toastify";
import {useTranslation} from "react-i18next";
import ConfirmCancelModal from "../../components/common/cancelModal/ConfirmCancelModal";
import PageHeader from "../../components/common/pageheader/PageHeader";
import {getExamBases} from "../../services/examBasesApi";
import {
    cancelExam,
    createExam,
    getExamAssignedStudentNumbers,
    getExamById,
    getExams,
    updateExam,
} from "../../services/examsApi";
import ExamFormModal from "./ExamFormModal/ExamFormModal";
import ExamConflictModal from "../examWizard/examConflictModal/ExamConflictModal";
import Select from "../../components/common/select/Select";
import styles from "./ExamPanelPage.module.css";
import Loader from "../../components/common/Loader/Loader";
import TableEmptyState from "../../components/common/tableEmptyState/TableEmptyState";
import {toStatusAz} from "../../utils/statusText";
import {formatDateTime} from "../../utils/formatDateTime";

const ExamPanelPage = () => {
    const {t} = useTranslation();
    const navigate = useNavigate();

    const [exams, setExams] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const [examBaseIdFilter, setExamBaseIdFilter] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [createdByFilter, setCreatedByFilter] = useState("");
    const [appliedFilters, setAppliedFilters] = useState({
        examBaseId: "",
        status: "",
        createdBy: "",
    });

    const [pageNo, setPageNo] = useState(1);
    const [pageSize] = useState(10);
    const [totalPages, setTotalPages] = useState(0);

    const [isFormModalOpen, setIsFormModalOpen] = useState(false);
    const [formMode, setFormMode] = useState("create");
    const [editingExam, setEditingExam] = useState(null);

    const [formValues, setFormValues] = useState({
        examBaseId: "",
        assignedStudentNumbers: "",
        scheduledStartTime: "",
        durationMinutes: "",
        passRate: "",
        note: "",
        proctored: false,
    });

    const [saving, setSaving] = useState(false);
    const [formError, setFormError] = useState("");
    // Conflict popup: the backend's list of students already booked at this time. The form modal
    // stays open underneath so the user keeps everything they filled while they pick a new time.
    const [scheduleConflicts, setScheduleConflicts] = useState(null);
    const startTimeRef = useRef(null);

    const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
    const [cancellingExam, setCancellingExam] = useState(null);
    const [cancelling, setCancelling] = useState(false);

    const [examBases, setExamBases] = useState([]);
    const [examBasesLoading, setExamBasesLoading] = useState(false);


    const [editingDurationMinutes, setEditingDurationMinutes] = useState(null);

    const toLocalInputValue = (isoString) => {
        if (!isoString) return "";
        const d = new Date(isoString);
        if (Number.isNaN(d.getTime())) return "";
        const pad = (n) => String(n).padStart(2, "0");
        const yyyy = d.getFullYear();
        const mm = pad(d.getMonth() + 1);
        const dd = pad(d.getDate());
        const hh = pad(d.getHours());
        const min = pad(d.getMinutes());
        return `${yyyy}-${mm}-${dd}T${hh}:${min}`;
    };

    const toIsoWithOffset = (localInputValue) => {
        if (!localInputValue) return "";
        const d = new Date(localInputValue);
        if (Number.isNaN(d.getTime())) return "";
        const pad = (n) => String(n).padStart(2, "0");
        const yyyy = d.getFullYear();
        const mm = pad(d.getMonth() + 1);
        const dd = pad(d.getDate());
        const hh = pad(d.getHours());
        const min = pad(d.getMinutes());
        const ss = pad(d.getSeconds());
        const tzMin = -d.getTimezoneOffset();
        const sign = tzMin >= 0 ? "+" : "-";
        const tzh = pad(Math.floor(Math.abs(tzMin) / 60));
        const tzm = pad(Math.abs(tzMin) % 60);
        return `${yyyy}-${mm}-${dd}T${hh}:${min}:${ss}${sign}${tzh}:${tzm}`;
    };

    const parseSelectedNumbers = (csv) => {
        if (!csv) return [];
        return csv
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean);
    };

    const safeNumberOrNull = (v) => {
        const n = Number(v);
        return Number.isFinite(n) ? n : null;
    };

    const fetchExams = async () => {
        setLoading(true);
        setError("");

        try {
            setAppliedFilters({
                examBaseId: examBaseIdFilter || "",
                status: statusFilter || "",
                createdBy: createdByFilter || "",
            });

            const data = await getExams({
                examBaseId: examBaseIdFilter || undefined,
                status: statusFilter || undefined,
                createdBy: createdByFilter || undefined,
                pageNo,
                pageSize,
            });

            setExams(data.content || []);
            setTotalPages(data.totalPages || 0);
        } catch (e) {
            setError(t("pages.exam.loadError"));
        } finally {
            setLoading(false);
        }
    };

    const fetchExamBasesForSelect = async () => {
        try {
            setExamBasesLoading(true);
            setError("");
            const data = await getExamBases({pageNo: 1, pageSize: 1000});
            setExamBases(Array.isArray(data?.content) ? data.content : []);
        } catch (err) {
            const msg = err?.response?.data?.message || err?.response?.data?.error || err.message || t("pages.exam.examBasesLoadError");
            setError(msg);
        } finally {
            setExamBasesLoading(false);
        }
    };

    useEffect(() => {
        fetchExams();
    }, [pageNo]);

    useEffect(() => {
        fetchExamBasesForSelect();
    }, []);

    const handleSearchSubmit = (e) => {
        e.preventDefault();
        setError("");
        if (pageNo !== 1) setPageNo(1);
        else fetchExams();
    };

    const resetForm = () => {
        setFormValues({
            examBaseId: "",
            assignedStudentNumbers: "",
            scheduledStartTime: "",
            durationMinutes: "",
            passRate: "",
            note: "",
            proctored: false,
        });
        setFormError("");
        setSaving(false);
        setEditingDurationMinutes(null);
    };

    const openCreateModal = async () => {
        setFormMode("create");
        setEditingExam(null);
        resetForm();
        await fetchExamBasesForSelect();
        setIsFormModalOpen(true);
    };

    const openEditModal = async (examRow) => {
        setFormMode("edit");
        setEditingExam(examRow);
        setFormError("");
        setSaving(false);

        try {
            await fetchExamBasesForSelect();

            const data = await getExamById(examRow.id);

            const exam = data?.exam ?? data ?? {};
            const examBaseId = String(data?.examBase?.id ?? exam?.examBase?.id ?? "");
            const duration = safeNumberOrNull(data?.examBase?.durationMinutes ?? exam?.examBase?.durationMinutes);

            // The exam detail payload no longer carries the student list (it is paged on the results
            // page); this form needs the whole assignment, so it asks for just the numbers.
            const assignedNumbers = await getExamAssignedStudentNumbers(examRow.id);
            const selectedNums = Array.isArray(assignedNumbers) ? assignedNumbers.map(String) : [];

            setEditingDurationMinutes(duration);

            setFormValues({
                examBaseId,
                assignedStudentNumbers: selectedNums.join(", "),
                scheduledStartTime: toLocalInputValue(exam.scheduledStartTime ?? examRow.scheduledStartTime),
                durationMinutes: exam.durationMinutes ?? examRow.durationMinutes ?? "",
                passRate: exam.passRate ?? examRow.passRate ?? "",
                note: exam.note ?? examRow.note ?? "",
                proctored: (exam.proctored ?? examRow.proctored) === true,
            });
        } catch (err) {
            const msg = err?.response?.data?.message || err?.response?.data?.error || err.message || t("pages.exam.examDetailsLoadError");
            setFormError(msg);
        } finally {
            setIsFormModalOpen(true);
        }
    };

    const closeFormModal = () => {
        setIsFormModalOpen(false);
        setEditingExam(null);
        resetForm();
    };

    const handleFormChange = async (e) => {
        const {name, type, value, checked} = e.target;
        const nextValue = type === "checkbox" ? checked : value;

        if (name === "examBaseId") {
            const found = nextValue
                ? examBases.find((b) => String(b.id) === String(nextValue))
                : null;
            const templateDuration = found?.durationMinutes ?? found?.defaultDurationMinutes;
            const templatePassRate = found?.passRate ?? found?.defaultPassRate;

            setFormValues((prev) => ({
                ...prev,
                examBaseId: nextValue,
                assignedStudentNumbers: "",
                durationMinutes:
                    formMode === "create" && templateDuration != null
                        ? String(templateDuration)
                        : prev.durationMinutes,
                passRate:
                    formMode === "create" && templatePassRate != null
                        ? String(templatePassRate)
                        : prev.passRate,
            }));

            setFormError("");

            if (!nextValue) {
                setEditingDurationMinutes(null);
                return;
            }

            // The eligible-student list is fetched (paged) by the modal itself off values.examBaseId.
            setEditingDurationMinutes(templateDuration != null ? Number(templateDuration) : null);
            return;
        }

        setFormValues((prev) => ({
            ...prev,
            [name]: nextValue,
        }));
    };

    const handleToggleStudent = (studentNumber) => {
        setFormValues((prev) => {
            const current = parseSelectedNumbers(prev.assignedStudentNumbers);
            const sn = String(studentNumber);
            const exists = current.includes(sn);
            const next = exists ? current.filter((n) => n !== sn) : [...current, sn];
            return {...prev, assignedStudentNumbers: next.join(", ")};
        });
    };

    const handleSelectAllStudents = (studentNumbers, shouldSelect) => {
        setFormValues((prev) => {
            const set = new Set(parseSelectedNumbers(prev.assignedStudentNumbers));
            studentNumbers.forEach((sn) => {
                const key = String(sn);
                if (shouldSelect) set.add(key);
                else set.delete(key);
            });
            return {...prev, assignedStudentNumbers: Array.from(set).join(", ")};
        });
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setFormError("");

        const assigned = parseSelectedNumbers(formValues.assignedStudentNumbers);

        const payload = {
            assignedStudentNumbers: assigned,
            scheduledStartTime: toIsoWithOffset(formValues.scheduledStartTime),
            durationMinutes: formValues.durationMinutes,
            passRate: formValues.passRate,
            note: formValues.note,
            proctored: formValues.proctored === true,
        };

        try {
            if (formMode === "edit" && editingExam) {
                await updateExam({id: editingExam.id, ...payload});
                toast.success(t("pages.exam.updateSuccess"));
            } else {
                await createExam({
                    examBaseId: Number(formValues.examBaseId),
                    assignedStudentNumbers: payload.assignedStudentNumbers,
                    scheduledStartTime: payload.scheduledStartTime,
                    durationMinutes: formValues.durationMinutes,
                    passRate: formValues.passRate,
                    note: payload.note,
                    proctored: payload.proctored,
                });
                toast.success(t("pages.exam.createSuccess"));
            }

            closeFormModal();
            fetchExams();
        } catch (err) {
            // Schedule clashes get the detailed popup (who's busy, with what, when). The form modal
            // stays open behind it so nothing the user entered is lost. Any other error → inline banner.
            const data = err?.response?.data;
            if (data?.code === "STUDENT_SCHEDULE_CONFLICT" && Array.isArray(data.conflicts)) {
                setScheduleConflicts(data.conflicts);
            } else {
                const msg = data?.message || data?.error || err.message || t("pages.exam.operationFailed");
                setFormError(msg);
            }
        } finally {
            setSaving(false);
        }
    };

    // Close the conflict popup and jump the user to the start-time field in the (still-open) form.
    const handleChangeTimeFromConflict = () => {
        setScheduleConflicts(null);
        requestAnimationFrame(() => {
            const el = startTimeRef.current;
            if (el) {
                el.scrollIntoView({behavior: "smooth", block: "center"});
                el.focus();
            }
        });
    };

    const openCancelModal = (exam) => {
        setError("");
        setCancellingExam(exam);
        setIsCancelModalOpen(true);
    };

    const closeCancelModal = () => {
        setCancellingExam(null);
        setIsCancelModalOpen(false);
        setCancelling(false);
    };

    const handleConfirmCancel = async () => {
        if (!cancellingExam) return;
        try {
            setCancelling(true);
            setError("");
            await cancelExam(cancellingExam.id);
            closeCancelModal();
            fetchExams();
            toast.success(t("pages.exam.cancelSuccess"));
        } catch (err) {
            const msg = err?.response?.data?.message || err?.response?.data?.error || err.message || t("pages.exam.cancelError");
            setError(msg);
        } finally {
            setCancelling(false);
        }
    };

    const goToPrevPage = () => setPageNo((prev) => Math.max(prev - 1, 1));
    const goToNextPage = () => setPageNo((prev) => (prev < totalPages ? prev + 1 : prev));

    const examBaseOptions = useMemo(() => {
        return (Array.isArray(examBases) ? examBases : []).map((eb) => ({id: eb.id, title: eb.title}));
    }, [examBases]);

    return (
        <div className={styles.container}>
            <PageHeader title={t("pages.exam.title")} icon={<FiCalendar/>}
                        description={t("pages.exam.subtitle")}/>

            <form className={styles.filterForm} onSubmit={handleSearchSubmit}>
                <Select
                    className={styles.filterInput}
                    value={examBaseIdFilter}
                    onChange={(e) => setExamBaseIdFilter(e.target.value)}
                    options={[
                        { value: "", label: t("pages.exam.selectExamBase") },
                        ...examBaseOptions.map((eb) => ({ value: eb.id, label: eb.title })),
                    ]}
                />

                <Select
                    className={styles.filterInput}
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    options={[
                        { value: "", label: t("pages.exam.statusFilter") },
                        { value: "ACTIVE", label: toStatusAz("ACTIVE") },
                        { value: "EXAM_CANCELLED", label: toStatusAz("EXAM_CANCELLED") },
                        { value: "FINISHED", label: toStatusAz("FINISHED") },
                    ]}
                />

                <input
                    type="text"
                    placeholder={t("pages.exam.createdByPlaceholder")}
                    className={styles.filterInput}
                    value={createdByFilter}
                    onChange={(e) => setCreatedByFilter(e.target.value)}
                />

                <button type="submit" className={styles.primaryButton}>
                    {t("common.search")}
                </button>

                <button type="button" className={styles.secondaryButton} onClick={openCreateModal}>
                    <FiPlus/>
                    {t("pages.exam.newExam")}
                </button>
            </form>

            {error && <div className={styles.error}>{error}</div>}

            <div className={styles.tableWrapper}>
                {loading ? (
                    <Loader fullscreen/>
                ) : exams.length === 0 ? (
                    <TableEmptyState
                        title={t("pages.exam.noExamsFound")}
                        description={appliedFilters.examBaseId || appliedFilters.status || appliedFilters.createdBy ? t("pages.exam.changeCriteria") : t("pages.exam.createNew")}
                        icon={appliedFilters.examBaseId || appliedFilters.status || appliedFilters.createdBy ? "search" : "inbox"}
                    />
                ) : (
                    <table className={styles.table}>
                        <thead>
                        <tr>
                            <th>{t("pages.exam.tableName")}</th>
                            <th>{t("pages.exam.tableStart")}</th>
                            <th>{t("pages.exam.tableEnd")}</th>
                            <th>{t("pages.exam.statusFilter")}</th>
                            <th>{t("pages.exam.tableCreatedBy")}</th>
                            <th>{t("pages.exam.tableNote")}</th>
                            <th>{t("pages.exam.tableProctored")}</th>
                            <th></th>
                        </tr>
                        </thead>
                        <tbody>
                        {exams.map((exam) => (
                            <tr key={exam.id}>
                                <td>{exam.examName}</td>
                                <td>{formatDateTime(exam.scheduledStartTime)}</td>
                                <td>{formatDateTime(exam.scheduledEndTime)}</td>
                                <td>{toStatusAz(exam.status)}</td>
                                <td>{exam.createdBy}</td>
                                <td>{exam.note || "-"}</td>
                                <td>{exam.proctored ? t("pages.exam.yes") : t("pages.exam.no")}</td>
                                <td className={styles.actionsCell}>
                                    <div className={styles.actions}>
                                        <button type="button" className={styles.iconButton}
                                                onClick={() => navigate(`/exams/${exam.id}`)}>
                                            <FiArrowRightCircle size={17}/>
                                        </button>

                                        {exam.status === "ACTIVE" && (
                                            <button type="button" className={styles.iconButton}
                                                    onClick={() => openEditModal(exam)}>
                                                <FiEdit size={17}/>
                                            </button>)}

                                        {exam.status === "ACTIVE" && (
                                            <button type="button" className={styles.iconButtonDanger}
                                                    onClick={() => openCancelModal(exam)}>
                                                <FiSlash size={17}/>
                                            </button>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                )}
            </div>

            <div className={styles.pagination}>
                <button type="button" onClick={goToPrevPage} disabled={pageNo === 1}>
                    {t("common.previous")}
                </button>
                <span>
            {t("common.pageOf", {page: pageNo, total: totalPages || 1})}
          </span>
                <button type="button" onClick={goToNextPage} disabled={pageNo >= totalPages}>
                    {t("common.next2")}
                </button>
            </div>

            <ExamFormModal
                isOpen={isFormModalOpen}
                mode={formMode}
                values={formValues}
                onChange={handleFormChange}
                onClose={closeFormModal}
                onSubmit={handleFormSubmit}
                saving={saving}
                error={formError}
                examBases={examBases}
                examBasesLoading={examBasesLoading}
                onToggleStudent={handleToggleStudent}
                onSelectAll={handleSelectAllStudents}
                startTimeRef={startTimeRef}
            />

            <ExamConflictModal
                isOpen={!!scheduleConflicts}
                conflicts={scheduleConflicts || []}
                onClose={() => setScheduleConflicts(null)}
                onChangeTime={handleChangeTimeFromConflict}
            />

            {isCancelModalOpen && (
                <ConfirmCancelModal
                    isOpen={isCancelModalOpen}
                    onCancel={closeCancelModal}
                    onConfirm={handleConfirmCancel}
                    message={
                        cancellingExam ? t("pages.exam.cancelConfirm", {name: cancellingExam.examName}) : t("pages.exam.cancelConfirmFallback")
                    }
                    disabled={cancelling}
                />
            )}
        </div>
    );
};

export default ExamPanelPage;
